/**
 * MainBoatList manages the boat listing table that displays all tracks (vessel + AIS)
 * with real-time metrics including speed, SOG, and separation distance.
 * 
 * Responsibilities:
 * - Creating and populating the boat listing table
 * - Updating real-time metrics during race replay
 * - Managing vessel name editing with localStorage persistence
 * - Handling show/hide checkboxes for track visibility
 * - Coordinating with MainFileControls for save operations after name changes
 */

import { shortTimeFormat } from '../utils/utils.js';
import vesselDirModeIconUrl from '../../icons/north_small.svg?url';

export class MainBoatList {
  constructor(dataStore, mapModule, settings, raceTracksN = null) {
    this.dataStore = dataStore;
    this.mapModule = mapModule;
    this.settings = settings;
    this.raceTracksN = raceTracksN;
    this.legendElement = null;
    this.boatCheckAllCb = null;
    this.handicapIdx = 0;
    this.handicapDropdown = null;
    this._showingResults = false;
  }

  init() {
    this.legendElement = document.getElementById("legend");
    this.boatCheckAllCb = document.getElementById("boatCheckAllCb");
  }

  setRaceTracksN(raceTracksN) {
    this.raceTracksN = raceTracksN;
    if (raceTracksN) {
      raceTracksN.setHandicaps(this.settings, this.handicapIdx);
    }
  }

  populateHandicapSelector(selector = document.getElementById("handicapModeSelector")) {
    const handicapNames = this.settings.getHandicapNames();
    selector.innerHTML = '';
    for (let i = 0; i < handicapNames.length; i++) {
      const option = document.createElement("option");
      option.value = i;
      option.textContent = handicapNames[i];
      if (i === this.handicapIdx) {
        option.selected = true;
      }
      selector.appendChild(option);
    }
    if (this.handicapIdx >= handicapNames.length) {
      this.handicapIdx = 0;
      selector.value = 0;
      if (this.raceTracksN) {
        this.raceTracksN.setHandicaps(this.settings, this.handicapIdx);
        this.updateLiveResultAll();
      }
    }
  }

  createLegend() {
    if (!this.legendElement) {
      console.error("Legend element not found");
      return;
    }

    this.legendElement.innerHTML = "";

    const table = document.createElement("table");
    table.className = "legend-table boat-legend-table";

    const headerRow = document.createElement("tr");

    this.handicapDropdown = document.createElement("select");
    this.handicapDropdown.id = "handicapModeSelector";
    this.handicapDropdown.className = "boat-list-handicap-selector";
    this.handicapDropdown.title = "Select handicap for ranking"

    this.populateHandicapSelector(this.handicapDropdown);

    this.handicapDropdown.addEventListener("change", (event) => {
      this.handicapIdx = parseInt(event.target.value);
      if (this.raceTracksN) {
        this.raceTracksN.setHandicaps(this.settings, this.handicapIdx);
        this.updateLiveResultAll();
      }
    });

    headerRow.innerHTML = `
<td><input id="boatCheckAllCb" type="checkbox" checked="true"></td>
<td></td>
<td>Boat</td>
<td>Dist m</td>
<td>SOG</td>`;

    const deltaHeaderCell = document.createElement("td");
    deltaHeaderCell.appendChild(this.handicapDropdown);
    headerRow.appendChild(deltaHeaderCell);

    table.appendChild(headerRow);

    const tracks = this.dataStore.getRace().sail().getTracks();
    tracks.forEach((track, index) => {
      const color = track.getColor(this.settings);
      const name = track.getShipName(this.settings);

      track.setVisible(true);
      track.setColor(color);

      const row = document.createElement("tr");
      row.dataset.trackId = track.getId();

      const colorCell = document.createElement("td");
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = true;
      checkbox.style.accentColor = color;
      checkbox.addEventListener("change", () => {
        track.setVisible(checkbox.checked);
        row.classList.toggle("boat-legend-row-hidden", !checkbox.checked);
        this.mapModule.update();
      });
      colorCell.appendChild(checkbox);
      row.appendChild(colorCell);

      const followCell = document.createElement('td');
      followCell.id = `followIcon_${track.getId()}`;
      row.appendChild(followCell);
      
      const nameCell = document.createElement("td");
      nameCell.textContent = name;
      nameCell.className = "boat-legend-name-cell";
      nameCell.addEventListener("click", () => {
        if (this.mapModule.getFollowedVessel() === track) {
          this.mapModule.setFollowedVessel(this.mapModule.getOwnVessel());
        } else {
          this.mapModule.setFollowedVessel(track);
        }
        this.updateLegendAll();
      });
      row.appendChild(nameCell);

      const separationCell = document.createElement("td");
      separationCell.id = `progressBarSep_${track.getId()}`;
      row.appendChild(separationCell);

      const sogCell = document.createElement("td");
      sogCell.id = `progressBarSOG_${track.getId()}`;
      row.appendChild(sogCell);

      const deltaCell = document.createElement("td");
      deltaCell.id = `progressBarDelta_${track.getId()}`;
      row.appendChild(deltaCell);

      table.appendChild(row);
    });

    this.legendElement.appendChild(table);
    this.mapModule.updateSize();

    this.boatCheckAllCb = document.getElementById("boatCheckAllCb");
    if (this.boatCheckAllCb) {
      this.boatCheckAllCb.addEventListener("change", (event) => {
        const tracks = this.dataStore.getRace().sail().getTracks();
        tracks.forEach(track => {
          track.setVisible(event.target.checked);
          const row = table.querySelector(`tr[data-track-id="${track.getId()}"]`);
          if (row) {
            row.querySelector('input[type="checkbox"]').checked = event.target.checked;
            row.classList.toggle("boat-legend-row-hidden", !event.target.checked);
          }
        });
        this.mapModule.update();
      });
    }
  }

  updateLegend(track, ranking) {
    const frameIndex = track.getPlayIndex();

    if (frameIndex != undefined && frameIndex != null) {
      const frame = track.getFrames()[frameIndex];
      if (frame != null) {
        let c = document.getElementById(`progressBarSOG_${track.getId()}`);
        if (c) {
          let v = frame.sog != null ? parseFloat(frame.sog).toFixed(2) : "-";
          c.innerHTML = v;
        }
        c = document.getElementById(`progressBarSep_${track.getId()}`);
        if (c) {
          let v = this.mapModule.calculateSeparationMeters(track);
          c.innerHTML = v;
        }
        c = document.getElementById(`progressBarDelta_${track.getId()}`);
        if (c) {
          if (ranking != null) {
            const legId = ranking.legId;
            const legColor = legId === 'S' ? 'green' : legId === 'F' ? 'red' : '#888';
            const legLabel = legId === undefined || legId === null ? '*' : String(legId);
            const deltaDisplay = ranking.delta !== null
              ? this.formatTimeDelta(ranking.delta)
              : '--';
            c.innerHTML = `<span style="color:${legColor}">${legLabel}</span> ${deltaDisplay}`;
          } else {
            c.innerHTML = ``;
          }
        }
      } else {
        console.warn("Passed a null frame");
      }
    }
  }

  formatTimeDelta(seconds) {
    if (seconds == null || !isFinite(seconds)) return "\u2014";
    if (seconds === 0) return "0s";
    return shortTimeFormat(seconds);
  }

  updateLiveResultAll() {
    if (!this.raceTracksN) {
      return;
    }
    let rankings = this.raceTracksN.getCurrentRaceRankings();
    if (rankings != null && rankings.length > 0) {
      if (this.handicapDropdown != null)
        this.handicapDropdown.style.display = 'flex';
      for (const ranking of rankings) {
        this.updateLegend(ranking.track, ranking);
      }

      const table = this.legendElement?.querySelector("table");
      if (table) {
        const currentRows = Array.from(table.querySelectorAll("tr")).slice(1);
        const rowMap = new Map();
        for (const row of currentRows) {
          rowMap.set(row.dataset.trackId, row);
        }

        const sortedRows = rankings.map(r => rowMap.get(String(r.track.getId()))).filter(r => r);

        if (this.rowsHaveDifferentOrder(currentRows, sortedRows)) {
          for (const row of sortedRows) {
            table.appendChild(row);
          }
        }

      }
    } else {
      if (this.handicapDropdown != null)
        this.handicapDropdown.style.display = 'none';
      for (const track of this.raceTracksN.getTracks()) {
        this.updateLegend(track, null);
      }
    }
  }

  updateLegendAll() {
    if (!this.raceTracksN) return;

    if (this.raceTracksN.allBoatsFinished()) {
      if (!this._showingResults) {
        this._showingResults = true;
        this._renderResultTable();
      }
    } else {
      if (this._showingResults) {
        this._showingResults = false;
        this.createLegend();
      }
      this.updateLiveResultAll();
    }
    this._updateFollowIcons();
  }

  _updateFollowIcons() {
    const followedVessel = this.mapModule.getFollowedVessel();
    const tracks = this.dataStore.getRace().sail().getTracks();
    tracks.forEach(track => {
      const cell = document.getElementById(`followIcon_${track.getId()}`);
      if (!cell) return;
      if (track === followedVessel) {
        cell.innerHTML = '<img src="'+vesselDirModeIconUrl+'" class="boat-list-follow-icon">';
      } else {
        cell.innerHTML = '';
      }
    });
  }

  _renderResultTable() {
    this.legendElement.innerHTML = '';

    const table = document.createElement('table');
    table.className = 'legend-table race-result-table';

    const headerRow = document.createElement('tr');
    const handicapSel = document.createElement('select');
    handicapSel.className = 'boat-list-handicap-selector';
    this.populateHandicapSelector(handicapSel);
    handicapSel.addEventListener('change', (event) => {
      this.handicapIdx = parseInt(event.target.value);
      if (this.raceTracksN) {
        this.raceTracksN.setHandicaps(this.settings, this.handicapIdx);
        this._renderResultTable();
      }
    });

    const handicapHeaderCell = document.createElement('td');
    handicapHeaderCell.appendChild(handicapSel);

    const colorHeaderCell = document.createElement('td');
    headerRow.appendChild(colorHeaderCell);
    const boatHeaderCell = document.createElement('td');
    boatHeaderCell.textContent = 'Boat';
    boatHeaderCell.className = 'race-result-boat-column';
    headerRow.appendChild(boatHeaderCell);
    headerRow.appendChild(handicapHeaderCell);
    const handicapName = this.settings.getHandicapNames()[this.handicapIdx];
    const timeHeader = handicapName === 'Line' ? 'Elapsed' : 'Corr';
    ['Finish', timeHeader].forEach(label => {
      const td = document.createElement('td');
      td.textContent = label;
      headerRow.appendChild(td);
    });
    table.appendChild(headerRow);

    const results = this.raceTracksN.getFinishResults();
    for (const result of results) {
      const row = document.createElement('tr');
      const color = result.track.getColor(this.settings);
      const handicap = result.track.getHandicap();
      const handicapStr = handicap != null ? handicap.toFixed(3) : '--';

      const colorCell = document.createElement('td');
      colorCell.innerHTML = `<span class="race-result-color-swatch" style="background:${color}"></span>`;
      row.appendChild(colorCell);

      const nameCell = document.createElement('td');
      nameCell.textContent = result.track.getShipName(this.settings);
      nameCell.className = 'race-result-boat-column';
      row.appendChild(nameCell);

      const handicapCell = document.createElement('td');
      handicapCell.textContent = handicapStr;
      row.appendChild(handicapCell);

      const timesCell = `
        <td>${result.finishTime == null ? "--" : result.finishTime.format('HH:mm:ss')}</td>
        <td>${result.correctedSec == null ? "--" : this._formatHHMMSS(Math.round(result.correctedSec))}</td>`;
      row.insertAdjacentHTML('beforeend', timesCell);

      table.appendChild(row);
    }

    this.legendElement.appendChild(table);
  }

  _formatHHMMSS(totalSec) {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = Math.floor(totalSec % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  rowsHaveDifferentOrder(currentRows, sortedRows) {
    if (currentRows.length !== sortedRows.length) return true;
    for (let i = 0; i < currentRows.length; i++) {
      const currentId = currentRows[i].dataset.trackId;
      const sortedId = sortedRows[i].dataset.trackId;
      if (currentId !== sortedId) return true;
    }
    return false;
  }

}
